import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import PageWrapper from '../layout/PageWrapper';

export default function ParentDirect() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Month to page mapping for the 2025-2026 school year calendar
  // Page 1 = Cover, Page 2 = September 2025, Last page = August 2026
  const monthToPage: { [key: string]: number } = {
    'September 2025': 2,
    'October 2025': 3,
    'November 2025': 4,
    'December 2025': 5,
    'January 2026': 6,
    'February 2026': 7,
    'March 2026': 8,
    'April 2026': 9,
    'May 2026': 10,
    'June 2026': 11,
    'July 2026': 12,
    'August 2026': 13
  };

  const getCurrentMonthPage = () => {
    const now = new Date();
    const currentMonth = now.toLocaleString('default', { month: 'long' });
    const currentYear = now.getFullYear();
    const monthKey = `${currentMonth} ${currentYear}`;
    
    // If current date is within the school year (Sep 2025 - Aug 2026), show that month
    // Otherwise default to September 2025 (start of school year) or cover page
    return monthToPage[monthKey] || 2; // Default to September 2025 instead of cover
  };

  useEffect(() => {
    setCurrentPage(getCurrentMonthPage());
  }, []);

  const pdfUrl = `/documents/2025-2026 CALENDAR.pdf#page=${currentPage}`;

  const months = [
    'September 2025', 'October 2025', 'November 2025', 'December 2025',
    'January 2026', 'February 2026', 'March 2026', 'April 2026',
    'May 2026', 'June 2026', 'July 2026', 'August 2026'
  ];

  // Also include cover page option
  const allOptions = [
    { label: 'Cover Page', value: 'cover', page: 1 },
    ...months.map(month => ({ label: month, value: month, page: monthToPage[month] }))
  ];

  // Simple password protection (can maybe switch to a more secure method in the future)
  const correctPassword = 'hccparents2025';

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === correctPassword) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  if (!isAuthenticated) {
    return (
      <PageWrapper>
        <div className="min-h-screen flex items-center justify-center">
          <motion.div
            className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-6">
              <div className="bg-dblue p-4 rounded-full mx-auto w-16 h-16 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-dblue mb-2">Parent Direct Access</h1>
              <p className="text-gray-600">This area is password protected for current parents only.</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dblue focus:border-dblue transition-colors"
                  placeholder="Enter password"
                  required
                />
              </div>

              {error && (
                <motion.div
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {error}
                </motion.div>
              )}

              <motion.button
                type="submit"
                className="w-full bg-dblue text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Access Parent Portal
              </motion.button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                For password assistance, please contact the front desk at 604.986.2388
              </p>
            </div>
          </motion.div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <motion.div
        className="max-w-6xl mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-dblue mb-2">
              Parent Direct Portal
            </h1>
            <p className="text-gray-600">
              Important documents and resources for current families
            </p>
          </motion.div>
        </div>

        {/* PDF Container */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-dblue">
                  Parent Handbook
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Complete guide for parents and families
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => window.open('/documents/parent-handbook.pdf', '_blank')}
                  className="px-4 py-2 bg-dblue text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  Open in New Tab
                </button>
                <a
                  href="/documents/PARENT_HANDBOOK.pdf"
                  download="HCC-Parent-Handbook.pdf"
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm"
                >
                  Download PDF
                </a>
              </div>
            </div>
          </div>

          {/* PDF Viewer */}
          <div className="p-6">
            <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '800px' }}>
              <iframe
                src="/documents/PARENT_HANDBOOK.pdf"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="Parent Handbook PDF"
              >
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-gray-600 mb-4">
                      Your browser doesn't support PDF viewing.
                    </p>
                    <a
                      href="/documents/PARENT_HANDBOOK.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-dblue text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download PDF
                    </a>
                  </div>
                </div>
              </iframe>
            </div>
          </div>

          {/* Additional Resources */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-dblue mb-4">Additional Resources</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setShowCalendar(true)}
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow text-left group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2 group-hover:text-dblue transition-colors">
                      📅 Calendar & Events
                    </h4>
                    <p className="text-sm text-gray-600">2025-2026 school year calendar</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-dblue transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-medium text-gray-800 mb-2">📢 Announcements</h4>
                <p className="text-sm text-gray-600">Latest announcements and centre changes</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Calendar Modal */}
        {showCalendar && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCalendar(false)}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Calendar Header */}
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-dblue to-blue-600">
                <div className="flex items-center justify-between">
                  <div className="text-white">
                    <h2 className="text-2xl font-bold mb-1">📅 School Calendar</h2>
                    <p className="text-blue-100">2025-2026 Academic Year</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Month Navigation */}
                    <select
                      value={allOptions.find(option => option.page === currentPage)?.value || 'cover'}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        if (selectedValue === 'cover') {
                          setCurrentPage(1);
                        } else {
                          const pageNumber = monthToPage[selectedValue] || 2;
                          setCurrentPage(pageNumber);
                        }
                      }}
                      className="px-3 py-2 bg-white text-dblue rounded-lg border border-gray-200 text-sm font-medium"
                    >
                      {allOptions.map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => window.open(pdfUrl, '_blank')}
                        className="px-4 py-2 bg-white text-dblue rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                      >
                        Open in New Tab
                      </button>
                      <a
                        href="/documents/2025-2026 CALENDAR.pdf"
                        download="HCC-Calendar-2025-2026.pdf"
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium"
                      >
                        Download PDF
                      </a>
                      <button
                        onClick={() => setShowCalendar(false)}
                        className="px-3 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Calendar PDF Viewer */}
              <div className="p-6">
                <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '70vh' }}>
                  <iframe
                    key={currentPage}
                    src={pdfUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    title="School Calendar 2025-2026"
                  >
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center p-8">
                        <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-gray-600 mb-4">
                          Your browser doesn't support PDF viewing.
                        </p>
                        <a
                          href="/documents/2025-2026 CALENDAR.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-dblue text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download Calendar PDF
                        </a>
                      </div>
                    </div>
                  </iframe>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </PageWrapper>
  );
}
